import React, { useState } from 'react';
import { Button } from './ui/button';
import { CreditCard } from 'lucide-react';

const PaymentButton = ({ packageData, onSuccess }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    const scriptLoaded = await loadRazorpayScript();

    if (!scriptLoaded) {
      alert('Failed to load Razorpay SDK. Please check your internet connection.');
      return;
    }

    setIsProcessing(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Get customer details from localStorage or prompt
      const customerName = prompt('Please enter your name:');
      const customerEmail = prompt('Please enter your email:');
      const customerPhone = prompt('Please enter your phone number:');

      if (!customerName || !customerEmail || !customerPhone) {
        setIsProcessing(false);
        return;
      }

      // Create order
      const orderResponse = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: packageData.price,
          package_name: packageData.name,
          customer_name: customerName,
          customer_email: customerEmail,
          customer_phone: customerPhone,
        }),
      });

      const orderData = await orderResponse.json();

      // Razorpay options
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'TUM Holidays',
        description: packageData.name,
        image: 'https://customer-assets.emergentagent.com/job_1c7269e2-1feb-426d-b007-6b7d8cae6132/artifacts/w1x3gw05_tum%20logo%20square.jpeg',
        order_id: orderData.order_id,
        handler: async function (response) {
          // Verify payment
          try {
            const verifyResponse = await fetch(`${BACKEND_URL}/api/payment/verify`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                booking_details: {
                  package: packageData.name,
                  amount: packageData.price,
                },
              }),
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.status === 'success') {
              alert('Payment successful! Your booking is confirmed.');
              if (onSuccess) {
                onSuccess(response);
              }
            }
          } catch (error) {
            console.error('Payment verification failed:', error);
            alert('Payment verification failed. Please contact support.');
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: customerName,
          email: customerEmail,
          contact: customerPhone,
        },
        notes: {
          package: packageData.name,
          duration: packageData.duration,
        },
        theme: {
          color: '#EF4444',
        },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Error initiating payment:', error);
      alert('Failed to initiate payment. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <Button
      onClick={handlePayment}
      disabled={isProcessing}
      className="w-full bg-green-600 hover:bg-green-700 text-white transition-all duration-300 hover:scale-105"
    >
      {isProcessing ? (
        <>
          <span className="animate-spin mr-2">⏳</span>
          Processing...
        </>
      ) : (
        <>
          <CreditCard className="mr-2" size={18} />
          Pay Now ₹{packageData.price.toLocaleString()}
        </>
      )}
    </Button>
  );
};

export default PaymentButton;
